/**
 * Socket ownership contract
 *
 * `ManagedSocket` owns the effective registration on the raw socket: callback
 * list, global join refcount and rebinding after a raw socket rebuild.
 *
 * `SocketConsumer` (returned by `SocketManager.getSocket`) owns the
 * subscription: every call site gets its own handle. Its `disconnect()` removes
 * only that consumer's listeners and releases only the joins that consumer
 * claimed; it never clears callbacks or joins owned by other consumers.
 * A join claim is local and idempotent — the manager stores and replays joins
 * on reconnect, so a queued join is never discarded and never claimed twice.
 */
import { createContext } from "react";
import openSocket from "socket.io-client";
import jwt from "jsonwebtoken";
import {
  invalidateSession,
  registerSocketCleanupHandler,
} from "../../services/sessionInvalidation";
import axios from "axios";
import { readToken, writeToken } from "../../services/tokenStorage";
import { buildSocketOptions } from "./socketOptions";

const SOCKET_DEBUG_ENABLED =
  String(process.env.REACT_APP_SOCKET_DEBUG || "").toLowerCase() === "true";

const AUTH_EXPIRED_ERROR = "ERR_SESSION_EXPIRED";
const AUTH_ERROR_PATTERNS = [
  AUTH_EXPIRED_ERROR,
  "jwt",
  "token",
  "unauthorized",
];
const RECOVERABLE_DISCONNECT_REASONS = new Set([
  "ping timeout",
  "transport close",
  "transport error",
]);
const NON_RECOVERABLE_DISCONNECT_REASONS = new Set([
  "io server disconnect",
  "io client disconnect",
]);

const buildJoinKey = (event, params) =>
  `${event}:${JSON.stringify(params || [])}`;

const normalizeSocketContextId = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  const normalized = String(value).trim();

  if (
    !normalized ||
    normalized === "-1" ||
    normalized === "undefined" ||
    normalized === "null"
  ) {
    return null;
  }

  return normalized;
};

const shouldRebuildSocket = (
  previousCompanyId,
  previousUserId,
  nextCompanyId,
  nextUserId,
) => {
  const previousCompany = normalizeSocketContextId(previousCompanyId);
  const previousUser = normalizeSocketContextId(previousUserId);
  const nextCompany = normalizeSocketContextId(nextCompanyId);
  const nextUser = normalizeSocketContextId(nextUserId);

  if (!previousCompany || !previousUser || !nextCompany || !nextUser) {
    return false;
  }

  return previousCompany !== nextCompany || previousUser !== nextUser;
};

const parseStoredToken = () => readToken();

const isTokenExpired = (token) => {
  if (!token) return true;
  const { exp } = jwt.decode(token) ?? {};
  if (!exp) return true;
  return Date.now() >= exp * 1000;
};

const refreshTokenOrLogout = async () => {
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/refresh_token`,
      {},
      { withCredentials: true },
    );

    if (data?.token) {
      writeToken(data.token);
      return data.token;
    }

    console.warn("[socket] sessão inválida por refresh inválido");
    invalidateSession({ reason: "socket refresh returned no token" });
    return null;
  } catch (error) {
    const status = error?.response?.status;
    const backendError = error?.response?.data?.error;

    if (
      status === 401 ||
      status === 403 ||
      backendError === AUTH_EXPIRED_ERROR
    ) {
      console.warn("[socket] sessão inválida por refresh inválido");
      invalidateSession({ reason: "socket refresh unauthorized" });
      return null;
    }

    throw error;
  }
};

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = null;
    this.callbacks = [];
    this.joins = new Map();
    this.refreshJoinsOnReady = null;
    this.buildJoinKey = buildJoinKey;
    this.flushStoredJoins = () => {
      if (!this.rawSocket) {
        return;
      }

      for (const join of this.joins.values()) {
        if (SOCKET_DEBUG_ENABLED) {
          console.debug("refreshing join", join);
        }
        this.rawSocket.emit(`join${join.event}`, ...join.params);
      }
    };
    this.handleConnect = () => {
      if (!this.rawSocket || this.rawSocket.recovered) {
        return;
      }

      if (this.socketManager.socketReady) {
        this.flushStoredJoins();
        return;
      }

      if (this.refreshJoinsOnReady) {
        this.rawSocket.off("ready", this.refreshJoinsOnReady);
      }

      this.refreshJoinsOnReady = () => {
        this.flushStoredJoins();
        this.rawSocket.off("ready", this.refreshJoinsOnReady);
        this.refreshJoinsOnReady = null;
      };

      this.rawSocket.on("ready", this.refreshJoinsOnReady);
    };

    this.unsubscribeSocketRebuilt = this.socketManager.onSocketRebuilt(
      (socket) => {
        this.bindSocket(socket);
      },
    );

    if (this.socketManager.currentSocket) {
      this.bindSocket(this.socketManager.currentSocket);
    }
  }

  bindSocket(socket) {
    if (!socket || this.rawSocket === socket) {
      return;
    }

    if (this.rawSocket) {
      this.rawSocket.off("connect", this.handleConnect);
      if (this.refreshJoinsOnReady) {
        this.rawSocket.off("ready", this.refreshJoinsOnReady);
      }
      for (const c of this.callbacks) {
        this.rawSocket.off(c.event, c.callback);
      }
    }

    this.rawSocket = socket;

    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
      this.rawSocket.on(c.event, c.callback);
    }

    this.rawSocket.off("connect", this.handleConnect);
    this.rawSocket.on("connect", this.handleConnect);

    if (this.rawSocket.connected) {
      this.handleConnect();
    }
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback);
    }
    this.callbacks.push({ event, callback });
    if (this.rawSocket) {
      return this.rawSocket.on(event, callback);
    }

    return undefined;
  }

  off(event, callback) {
    const i = this.callbacks.findIndex(
      (c) => c.event === event && c.callback === callback,
    );

    if (i >= 0) {
      this.callbacks.splice(i, 1);
    }

    if (this.rawSocket) {
      return this.rawSocket.off(event, callback);
    }

    return undefined;
  }

  emit(event, ...params) {
    if (event.startsWith("join")) {
      const joinEvent = event.substring(4);
      const joinKey = this.buildJoinKey(joinEvent, params);
      const current = this.joins.get(joinKey);

      if (current) {
        current.count += 1;
      } else {
        this.joins.set(joinKey, { event: joinEvent, params, count: 1 });
      }

      if (SOCKET_DEBUG_ENABLED) {
        console.debug("Joining", { event: joinEvent, params });
      }
      if (this.rawSocket && this.rawSocket.connected && this.socketManager.socketReady && !current) {
        return this.rawSocket.emit(event, ...params);
      }
      return undefined;
    }

    if (event.startsWith("leave")) {
      const leaveEvent = event.substring(5);
      const joinKey = this.buildJoinKey(leaveEvent, params);
      const current = this.joins.get(joinKey);

      if (current) {
        if (current.count > 1) {
          current.count -= 1;
        } else {
          this.joins.delete(joinKey);
          if (this.rawSocket && this.rawSocket.connected) {
            return this.rawSocket.emit(event, ...params);
          }
        }
      }

      return undefined;
    }
    if (this.rawSocket) {
      return this.rawSocket.emit(event, ...params);
    }

    return undefined;
  }

  disconnect() {
    if (this.unsubscribeSocketRebuilt) {
      this.unsubscribeSocketRebuilt();
      this.unsubscribeSocketRebuilt = null;
    }

    if (!this.rawSocket) {
      this.joins.clear();
      this.callbacks = [];
      return;
    }

    for (const join of this.joins.values()) {
      this.rawSocket.emit(`leave${join.event}`, ...join.params);
    }
    this.joins.clear();
    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
    this.rawSocket.off("connect", this.handleConnect);
    if (this.refreshJoinsOnReady) {
      this.rawSocket.off("ready", this.refreshJoinsOnReady);
      this.refreshJoinsOnReady = null;
    }
  }
}
class SocketConsumer {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.managed = null;
    this.closed = false;
    this.registrations = [];
    this.joinClaims = new Map();
    this.unsubscribeSocketRebuilt = socketManager.onSocketRebuilt(() => {
      this._resolveManaged();
    });

    this._resolveManaged();
  }

  get connected() {
    const managed = this._resolveManaged();

    return managed?.rawSocket?.connected === true;
  }

  _resolveManaged() {
    const nextManaged = this.socketManager.currentManagedSocket || null;

    if (nextManaged === this.managed) {
      return this.managed;
    }

    if (this.managed) {
      for (const registration of this.registrations) {
        this.managed.off(registration.event, registration.wrapper);
      }
    }

    this.managed = nextManaged;

    if (!this.managed || this.closed) {
      return this.managed;
    }

    for (const registration of this.registrations) {
      this.managed.on(registration.event, registration.wrapper);
    }

    for (const claim of this.joinClaims.values()) {
      this.managed.emit(`join${claim.event}`, ...claim.params);
    }

    return this.managed;
  }

  on(event, callback) {
    if (this.closed) {
      return undefined;
    }

    const managed = this._resolveManaged();

    if (event === "ready" || event === "connect") {
      return managed ? managed.on(event, callback) : undefined;
    }

    const wrapper = (...args) => callback(...args);
    this.registrations.push({ event, callback, wrapper });

    return managed ? managed.on(event, wrapper) : undefined;
  }

  off(event, callback) {
    const index = this.registrations.findIndex(
      (registration) =>
        registration.event === event && registration.callback === callback,
    );

    if (index < 0) {
      return undefined;
    }

    const [{ wrapper }] = this.registrations.splice(index, 1);

    return this.managed ? this.managed.off(event, wrapper) : undefined;
  }

  emit(event, ...params) {
    if (this.closed) {
      return undefined;
    }

    const managed = this._resolveManaged();

    if (event.startsWith("join")) {
      const joinEvent = event.substring(4);
      const joinKey = buildJoinKey(joinEvent, params);

      if (this.joinClaims.has(joinKey)) {
        return undefined;
      }

      this.joinClaims.set(joinKey, { event: joinEvent, params });

      return managed ? managed.emit(event, ...params) : undefined;
    }

    if (event.startsWith("leave")) {
      const leaveEvent = event.substring(5);
      const joinKey = buildJoinKey(leaveEvent, params);

      if (!this.joinClaims.has(joinKey)) {
        return undefined;
      }

      this.joinClaims.delete(joinKey);

      return managed ? managed.emit(event, ...params) : undefined;
    }

    return managed ? managed.emit(event, ...params) : undefined;
  }

  disconnect() {
    if (this.closed) {
      return;
    }

    this.closed = true;

    if (this.unsubscribeSocketRebuilt) {
      this.unsubscribeSocketRebuilt();
      this.unsubscribeSocketRebuilt = null;
    }

    for (const registration of this.registrations) {
      if (this.managed) {
        this.managed.off(registration.event, registration.wrapper);
      }
    }
    this.registrations = [];

    for (const claim of this.joinClaims.values()) {
      if (this.managed) {
        this.managed.emit(`leave${claim.event}`, ...claim.params);
      }
    }
    this.joinClaims.clear();
  }
}
class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  currentManagedSocket: null,
  socketReady: false,

  tokenRefreshPromise: null,
  socketSetupPromise: null,
  socketRebuildListeners: new Set(),
  refreshInvalidatedSession: false,
  lastConnectWasAfterRefresh: false,
  authReconnectInProgress: false,
  authReconnectAttemptedAfterRefresh: false,
  authReconnectCleanup: null,

  onSocketRebuilt: function (callback) {
    this.socketRebuildListeners.add(callback);
    return () => {
      this.socketRebuildListeners.delete(callback);
    };
  },

  notifySocketRebuilt: function () {
    for (const callback of this.socketRebuildListeners) {
      callback(this.currentSocket);
    }
  },

  updateSocketCredentials: function (token) {
    if (!this.currentSocket || !token) {
      return;
    }

    // socket.io re-sends `auth` on every reconnect attempt, so a refreshed
    // token reaches the handshake without putting the JWT in the URL.
    this.currentSocket.auth = { token };

    if (this.currentSocket.io?.opts?.query) {
      delete this.currentSocket.io.opts.query.token;
    }
  },

  clearManagedSocket: function () {
    if (this.currentManagedSocket) {
      this.currentManagedSocket.disconnect();
      this.currentManagedSocket = null;
    }
  },

  cleanupSocketState: function () {
    this.socketReady = false;
    this.clearManagedSocket();

    if (this.authReconnectCleanup) {
      this.authReconnectCleanup();
      this.authReconnectCleanup = null;
    }

    this.authReconnectInProgress = false;
    this.authReconnectAttemptedAfterRefresh = false;

    if (this.currentSocket) {
      this.currentSocket.removeAllListeners();
      this.currentSocket.disconnect();
    }

    this.currentSocket = null;
  },

  invalidateSession: function (reason) {
    console.warn("[socket] disconnect não-recuperável por sessão inválida", {
      reason,
    });
    this.refreshInvalidatedSession = true;
    this.lastConnectWasAfterRefresh = false;
    this.cleanupSocketState();
    invalidateSession({ reason: `socket ${reason}` });
  },

  ensureFreshToken: async function () {
    let token = parseStoredToken();
    if (token && !isTokenExpired(token)) {
      this.refreshInvalidatedSession = false;

      return token;
    }

    console.warn("[socket] token expirado, iniciando refresh");

    if (!this.tokenRefreshPromise) {
      this.tokenRefreshPromise = refreshTokenOrLogout()
        .then((refreshedToken) => {
          if (refreshedToken) {
            console.info("[socket] refresh concluído");
            this.refreshInvalidatedSession = false;
            this.lastConnectWasAfterRefresh = true;
            this.updateSocketCredentials(refreshedToken);
          } else {
            this.refreshInvalidatedSession = true;
            this.lastConnectWasAfterRefresh = false;
          }
          return refreshedToken;
        })
        .finally(() => {
          this.tokenRefreshPromise = null;
        });
    }

    token = await this.tokenRefreshPromise;
    return token;
  },

  reconnectAfterExpiredHandshake: async function () {
    if (this.authReconnectInProgress) {
      return;
    }

    this.authReconnectInProgress = true;

    try {
      const token = await this.ensureFreshToken();

      if (!token || isTokenExpired(token)) {
        this.invalidateSession("handshake token inválido");
        return;
      }

      if (!this.currentSocket) {
        return;
      }

      console.info("[socket] token refreshed", {
        hasToken: Boolean(token),
      });

      this.updateSocketCredentials(token);
      console.info("[socket] credentials updated");

      if (this.authReconnectCleanup) {
        this.authReconnectCleanup();
      }

      const socket = this.currentSocket;

      await new Promise((resolve, reject) => {
        const cleanup = () => {
          socket.off("connect", onConnect);
          socket.off("connect_error", onConnectError);
          if (this.authReconnectCleanup === cleanup) {
            this.authReconnectCleanup = null;
          }
        };

        const onConnect = () => {
          cleanup();
          resolve();
        };

        const onConnectError = (error) => {
          cleanup();
          reject(error);
        };

        this.authReconnectCleanup = cleanup;

        socket.off("connect", onConnect);
        socket.off("connect_error", onConnectError);
        socket.on("connect", onConnect);
        socket.on("connect_error", onConnectError);

        console.info("[socket] reconnecting");
        socket.connect();
      });

      console.info("[socket] reconnected");
      this.authReconnectAttemptedAfterRefresh = false;
    } catch (error) {
      const message = String(error?.message || "").toLowerCase();
      const isAuthError = AUTH_ERROR_PATTERNS.some((pattern) =>
        message.includes(pattern.toLowerCase()),
      );

      console.warn("[socket] reconnect failed", {
        message,
      });

      if (isAuthError) {
        this.invalidateSession("erro de autenticação após refresh");
      }
    } finally {
      this.authReconnectInProgress = false;
    }
  },

  ensureSocketConnection: function (companyId, userId) {
    if (this.socketSetupPromise) {
      return this.socketSetupPromise;
    }

    this.socketSetupPromise = (async () => {
      const token = await this.ensureFreshToken();

      if (!token || isTokenExpired(token)) {
        this.refreshInvalidatedSession = true;
        this.clearManagedSocket();
        return null;
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;

      this.currentSocket = openSocket(
        process.env.REACT_APP_BACKEND_URL,
        buildSocketOptions(token),
      );

      this.currentSocket.on("tokenRefreshed", (data) => {
        if (!data?.token) return;
        writeToken(data.token);
        this.updateSocketCredentials(data.token);
        console.info("[socket] token refreshed by server");
      });

      this.socketReady = false;
      this.notifySocketRebuilt();

      this.currentSocket.on("connect_error", async (error) => {
        const message = String(error?.message || "").toLowerCase();
        const isAuthError = AUTH_ERROR_PATTERNS.some((pattern) =>
          message.includes(pattern.toLowerCase()),
        );

        if (isAuthError) {
          if (this.authReconnectInProgress) {
            console.warn("[socket] auth error while reconnecting", {
              message,
            });
            return;
          }

          if (this.authReconnectAttemptedAfterRefresh) {
            this.invalidateSession(
              "erro de autenticação após tentativa de refresh",
            );
            return;
          }

          this.authReconnectAttemptedAfterRefresh = true;
          await this.reconnectAfterExpiredHandshake();
          return;
        }

        console.warn("[socket] connect_error recuperável, aguardando retry", {
          message,
        });
      });

      this.currentSocket.on("disconnect", async (reason) => {
        console.warn(`socket disconnected because: ${reason}`);
        this.socketReady = false;

        if (NON_RECOVERABLE_DISCONNECT_REASONS.has(reason)) {
          const token = parseStoredToken();

          if (token && !isTokenExpired(token)) {
            return;
          }

          try {
            const refreshedToken = await this.ensureFreshToken();
            if (refreshedToken && !isTokenExpired(refreshedToken)) {
              return;
            }
          } catch (error) {
            console.warn(
              "[socket] falha ao atualizar token após disconnect não-recuperável",
              {
                reason,
                error,
              },
            );
          }

          this.invalidateSession(reason);
          return;
        }

        if (!RECOVERABLE_DISCONNECT_REASONS.has(reason)) {
          return;
        }
        const currentToken = parseStoredToken();
        if (currentToken && !isTokenExpired(currentToken)) {
          return;
        }

        try {
          await this.ensureFreshToken();
        } catch (error) {
          console.warn("[socket] falha ao atualizar token após disconnect", {
            reason,
            error,
          });
        }
      });

      this.currentSocket.on("connect", (...params) => {
        this.authReconnectAttemptedAfterRefresh = false;
        if (this.lastConnectWasAfterRefresh) {
          console.info("[socket] socket conectado pós-refresh", params);
          this.lastConnectWasAfterRefresh = false;
        } else {
          console.info("socket connected", params);
        }
      });

      if (SOCKET_DEBUG_ENABLED) {
        this.currentSocket.onAny((event, ...args) => {
          console.debug("Event: ", { socket: this.currentSocket, event, args });
        });
      }

      this.onReady(() => {
        this.socketReady = true;
      });
      return this.currentSocket;
    })()
      .catch((error) => {
        console.error("[socket] falha ao preparar conexão", error);
        return null;
      })
      .finally(() => {
        this.socketSetupPromise = null;
      });

    return this.socketSetupPromise;
  },

  getSocket: function (companyId) {
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (
      shouldRebuildSocket(
        this.currentCompanyId,
        this.currentUserId,
        companyId,
        userId,
      )
    ) {
      if (this.currentSocket) {
        console.warn("closing old socket - company or user changed");
        this.socketReady = false;
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
      }
      this.clearManagedSocket();
      this.socketReady = false;
      this.refreshInvalidatedSession = false;
    }

    if (!this.currentSocket) {
      this.ensureSocketConnection(companyId, userId);
    }

    if (this.refreshInvalidatedSession) {
      return new DummySocket();
    }

    if (!this.currentManagedSocket) {
      this.currentManagedSocket = new ManagedSocket(this);
    }

    return new SocketConsumer(this);
  },

  onReady: function (callbackReady) {
    if (this.socketReady) {
      callbackReady();
      return;
    }

    if (!this.currentSocket) {
      const unsubscribe = this.onSocketRebuilt((socket) => {
        if (!socket) {
          return;
        }
        socket.once("ready", () => {
          callbackReady();
        });
        unsubscribe();
      });
      return;
    }

    this.currentSocket.once("ready", () => {
      callbackReady();
    });
  },
};

registerSocketCleanupHandler(() => {
  SocketManager.refreshInvalidatedSession = true;
  SocketManager.lastConnectWasAfterRefresh = false;
  SocketManager.cleanupSocketState();
});

const SocketContext = createContext();

export {
  SocketContext,
  SocketManager,
  ManagedSocket,
  SocketConsumer,
  shouldRebuildSocket,
};
